// SCR-STAFF-410 — چک‌ها (US-BIL-103؛ SM-Cheque): فهرست چک‌های دریافتی با وضعیت و نتیجه صیاد (نشان «نمونه» برای Adapter نمونه)، استعلام دوباره صیاد،
// تأیید دستی با کد پیگیری، واگذاری به بانک، وصول، برگشت (بدهی دوباره فعال)، عودت و لغو با دلیل؛ تاریخچه هر چک.
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { unwrap, type BilChequeStatus, type BilChequeView } from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, FormDialog, JalaliDate, ListPage, ReasonDialog, SampleBadge, type DataTableColumn } from '@phoenix/ui';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { Amount, bilKeys, ChequeStatusChip, SCR_STAFF_410 } from './common';

const STATUSES: readonly Exclude<BilChequeStatus, null>[] = ['PendingSayyad', 'Received', 'Deposited', 'Cleared', 'Bounced', 'Returned', 'Cancelled'];

type ChequeAction = 'verify-sayyad' | 'confirm' | 'deposit' | 'clear' | 'bounce' | 'return' | 'cancel';

export function ChequesPage() {
  const { t } = useTranslation('bil');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const queryClient = useQueryClient();
  const key = useIdempotencyKey();
  const [params] = useSearchParams();
  const patientId = params.get('patientId') ?? undefined;
  const [status, setStatus] = useState<BilChequeStatus | ''>('');
  const [pending, setPending] = useState<{ cheque: BilChequeView; action: ChequeAction } | null>(null);
  const [manual, setManual] = useState({ reference: '', note: '' });
  const [account, setAccount] = useState('');
  const cheques = useInfiniteQuery({
    queryKey: bilKeys.cheques(session?.tenantId, `${patientId ?? ''}|${status}`),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => unwrap(tenantApi.GET('/api/v1/billing/cheques', { params: { query: { patientId, status: status || undefined, cursor: pageParam, limit: 50 } } })),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const accounts = useQuery({
    queryKey: bilKeys.bankAccounts(session?.tenantId, pending?.cheque.branchId ?? ''),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/billing/bank-accounts', { params: { query: { branchId: pending?.cheque.branchId } } })),
    enabled: pending?.action === 'deposit',
  });
  const act = useMutation({
    mutationFn: async ({ cheque, action, reason }: { cheque: BilChequeView; action: ChequeAction; reason?: string }) => {
      const headers = { 'Idempotency-Key': key.current() };
      const path = { id: cheque.id };
      switch (action) {
        case 'verify-sayyad':
          return unwrap(tenantApi.POST('/api/v1/billing/cheques/{id}:verify-sayyad', { params: { path }, headers }));
        case 'confirm':
          return unwrap(tenantApi.POST('/api/v1/billing/cheques/{id}:confirm', { params: { path }, body: { reference: manual.reference.trim(), note: manual.note.trim() }, headers }));
        case 'deposit':
          return unwrap(tenantApi.POST('/api/v1/billing/cheques/{id}:deposit', { params: { path }, body: { bankAccountId: account, depositedOn: null }, headers }));
        case 'clear':
          return unwrap(tenantApi.POST('/api/v1/billing/cheques/{id}:clear', { params: { path }, body: { on: null, reason: null }, headers }));
        case 'bounce':
          return unwrap(tenantApi.POST('/api/v1/billing/cheques/{id}:bounce', { params: { path }, body: { on: null, reason: reason ?? null }, headers }));
        case 'return':
          return unwrap(tenantApi.POST('/api/v1/billing/cheques/{id}:return', { params: { path }, body: { on: null, reason: reason ?? null }, headers }));
        default:
          return unwrap(tenantApi.POST('/api/v1/billing/cheques/{id}:cancel', { params: { path }, body: { on: null, reason: reason ?? null }, headers }));
      }
    },
    onSettled: (_data, error) => key.settle(error ?? undefined),
    onSuccess: () => {
      setPending(null);
      setManual({ reference: '', note: '' });
      setAccount('');
      void queryClient.invalidateQueries({ queryKey: bilKeys.all(session?.tenantId) });
    },
  });
  const can = access.accessMode === 'Full' && access.can('bil.cheque.manage');
  const button = (cheque: BilChequeView, action: ChequeAction) => (
    <Button
      key={action}
      size="small"
      onClick={() => (action === 'verify-sayyad' || action === 'clear' ? act.mutate({ cheque, action }) : setPending({ cheque, action }))}
      data-testid={`bil-cheque-${action}`}
    >
      {t(`bil:cheques.actions.${action}`)}
    </Button>
  );
  const actions = (row: BilChequeView): ChequeAction[] => {
    switch (row.status) {
      case 'PendingSayyad':
        return ['verify-sayyad', 'confirm', 'cancel'];
      case 'Received':
        return ['deposit', 'return'];
      case 'Deposited':
        return ['clear', 'bounce'];
      case 'Bounced':
        return ['return'];
      default:
        return [];
    }
  };
  const columns: DataTableColumn<BilChequeView>[] = [
    { id: 'patient', headerKey: 'bil:deposits.columns.patient', cell: (row) => row.patientName ?? '—' },
    { id: 'sayyad', headerKey: 'bil:cheques.sayyad', cell: (row) => <bdi dir="ltr">{row.sayyadId}</bdi> },
    { id: 'bank', headerKey: 'bil:cheques.bank', cell: (row) => t(`bil:bank.${row.bankCode}`, { defaultValue: row.bankCode }) },
    { id: 'amount', headerKey: 'bil:cashier.amount', cell: (row) => <Amount money={row.amount} /> },
    { id: 'due', headerKey: 'bil:cheques.due', cell: (row) => <JalaliDate value={row.dueDate} /> },
    {
      id: 'sayyad_status',
      headerKey: 'bil:cheques.sayyad_status',
      cell: (row) => (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Typography variant="body2">{t(`bil:sayyad_status.${row.sayyadStatus}`)}</Typography>
          {row.sayyadIsSample ? <SampleBadge /> : null}
        </Stack>
      ),
    },
    { id: 'status', headerKey: 'bil:deposits.columns.status', cell: (row) => <ChequeStatusChip status={row.status} /> },
    {
      id: 'actions',
      headerKey: 'bil:deposits.columns.actions',
      cell: (row) => (can ? <Stack direction="row" spacing={0.5}>{actions(row).map((action) => button(row, action))}</Stack> : null),
    },
  ];
  const reasonAction = pending && (pending.action === 'bounce' || pending.action === 'return' || pending.action === 'cancel') ? pending : null;
  return (
    <ListPage
      screenId={SCR_STAFF_410}
      titleKey="bil:cheques.title"
      subtitleKey="bil:cheques.subtitle"
      filters={
        <TextField select size="small" label={t('bil:deposits.columns.status')} value={status} onChange={(event) => setStatus(event.target.value as BilChequeStatus | '')} sx={{ minWidth: 200 }}>
          <MenuItem value="">{t('bil:invoices.all')}</MenuItem>
          {STATUSES.map((value) => (
            <MenuItem key={value} value={value}>
              {t(`bil:cheque_status.${value}`)}
            </MenuItem>
          ))}
        </TextField>
      }
    >
      <DataTable
        captionKey="bil:cheques.title"
        columns={columns}
        rows={cheques.data?.pages.flatMap((page) => page.items)}
        getRowId={(row) => row.id}
        isLoading={cheques.isPending}
        error={cheques.error ?? act.error}
        onRetry={() => void cheques.refetch()}
        hasMore={cheques.hasNextPage}
        onLoadMore={() => void cheques.fetchNextPage()}
        isFetchingMore={cheques.isFetchingNextPage}
        emptyTitleKey="bil:cheques.empty"
      />
      <FormDialog
        open={pending?.action === 'confirm'}
        titleKey="bil:cheques.actions.confirm"
        onSubmit={() => pending && manual.reference.trim() !== '' && manual.note.trim() !== '' && act.mutate({ cheque: pending.cheque, action: 'confirm' })}
        onClose={() => setPending(null)}
        isDirty={manual.reference !== ''}
        isSubmitting={act.isPending}
        submitError={act.error}
      >
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField required label={t('bil:cheques.reference')} value={manual.reference} onChange={(event) => setManual({ ...manual, reference: event.target.value })} slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'bil-cheque-reference' } }} />
          <TextField required label={t('bil:write_off.reason')} value={manual.note} onChange={(event) => setManual({ ...manual, note: event.target.value })} slotProps={{ htmlInput: { 'data-testid': 'bil-cheque-note' } }} />
        </Stack>
      </FormDialog>
      <FormDialog
        open={pending?.action === 'deposit'}
        titleKey="bil:cheques.actions.deposit"
        onSubmit={() => pending && account !== '' && act.mutate({ cheque: pending.cheque, action: 'deposit' })}
        onClose={() => setPending(null)}
        isDirty={account !== ''}
        isSubmitting={act.isPending}
        submitError={act.error}
      >
        <TextField select fullWidth sx={{ mt: 1 }} required label={t('bil:bank_deposit.account')} value={account} onChange={(event) => setAccount(event.target.value)} slotProps={{ htmlInput: { 'data-testid': 'bil-cheque-account' } }}>
          {(accounts.data?.items ?? []).map((item) => (
            <MenuItem key={item.id} value={item.id}>
              {item.title} <bdi dir="ltr">{item.maskedNumber}</bdi>
            </MenuItem>
          ))}
        </TextField>
      </FormDialog>
      <ReasonDialog
        open={reasonAction !== null}
        titleKey={reasonAction ? `bil:cheques.actions.${reasonAction.action}` : 'bil:cheques.title'}
        busy={act.isPending}
        error={act.error}
        onConfirm={(reason) => reasonAction && act.mutate({ cheque: reasonAction.cheque, action: reasonAction.action, reason })}
        onCancel={() => setPending(null)}
      />
    </ListPage>
  );
}
